import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Phone, Mail, Shield, User, MapPin, TrendingUp, ChevronDown, HardHat, Truck, Building, Sparkles, Package, MessageCircle, Send, Loader2, CheckCircle, AlertTriangle, Sun, Wrench, TreePine, Shovel, Waves, Thermometer, Layers } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";

const WHATSAPP_NUMBER = "32470987076";
const PHONE_DISPLAY = "0470 98 70 76";
const EMAIL = "info@werkvloot.be";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google Analytics initialization
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

const initGoogleAnalytics = () => {
  if (GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX') {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID);
  }
};

// Floating WhatsApp Widget
const WhatsAppWidget = () => {
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=Hallo%2C%20ik%20zoek%20werfassistenten.`;
  
  return (
    <a
      href={whatsappLink}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-widget"
      className="fixed bottom-24 right-4 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-all hover:scale-110"
      aria-label="WhatsApp contact"
    >
      <MessageCircle className="w-7 h-7" />
    </a>
  );
};

// SECTION 1: HERO with Local Focus
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen bg-[#121212] overflow-hidden flex items-center">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&q=80"
          alt="Vlaamse bouwwerf bij dageraad"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#121212] via-[#121212]/95 to-[#121212]/70"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 md:px-8 py-24 md:py-32">
        <div className="max-w-4xl">
          {/* Main Headline */}
          <h1 data-testid="hero-title" className="font-oswald text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase tracking-tight leading-[1.1] mb-6">
            Het Fundament voor Uw Succes:
            <span className="block text-[#FF4F00] mt-2">De Vaste Rechterhand op de Vlaamse Werf.</span>
          </h1>
          
          {/* Sub-headline */}
          <p data-testid="hero-subtitle" className="text-lg md:text-xl text-[#E0E0E0] mb-10 max-w-3xl font-manrope leading-relaxed">
            Wij detacheren gemotiveerd eigen personeel dat meedraait in uw team. Geen dure vakmannen voor zwaar labeur, maar onmisbare werfassistenten. <strong className="text-white">Flexibel inzetbaar in uw regio:</strong> van één sterke handlanger tot een compleet team.
          </p>
          
          {/* CTA Button */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="tel:+32470987076"
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-3 bg-[#FF4F00] text-white font-bold text-lg uppercase tracking-wider px-8 py-5 industrial-btn hover:bg-[#E64500] transition-colors"
            >
              <Phone className="w-6 h-6" />
              Bel Direct voor Mankracht
            </a>
            <a 
              href="#diensten"
              data-testid="hero-services-btn"
              className="inline-flex items-center justify-center gap-3 border-2 border-white text-white font-bold text-lg uppercase tracking-wider px-8 py-5 hover:bg-white hover:text-[#121212] transition-colors"
            >
              Bekijk Onze Diensten
              <ChevronDown className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
      
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-8 h-8 text-[#FF4F00]" />
      </div>
    </section>
  );
};

// SECTION 2: TRUST BAR with Flexibility & Local SEO
const TrustBar = () => {
  const trustItems = [
    { icon: Shield, text: "100% Eigen Personeel" },
    { icon: User, text: "Vanaf 1 Persoon Inzetbaar" },
    { icon: MapPin, text: "Actief in West- & Oost-Vlaanderen" },
    { icon: TrendingUp, text: "Opleidingsgericht & Loyaal" }
  ];

  return (
    <section data-testid="trust-bar" className="bg-[#1A1A1A] py-8 border-y border-zinc-800">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {trustItems.map((item, index) => (
            <div key={index} className="flex items-center gap-3 justify-center">
              <div className="bg-[#FF4F00]/20 p-2 rounded">
                <item.icon className="w-6 h-6 text-[#FF4F00]" />
              </div>
              <span className="text-white font-medium text-sm md:text-base">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// SECTION 3: ONZE AANPAK
const AanpakSection = () => {
  return (
    <section id="aanpak" data-testid="aanpak-section" className="py-20 md:py-32 bg-[#121212]">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <div className="relative order-2 lg:order-1">
            <div className="relative overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1489514354504-1653aa90e34e?w=800&q=80"
                alt="WERKVLOOT assistent helpt vakman"
                className="w-full h-[400px] lg:h-[500px] object-cover grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>
            {/* Accent bar */}
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-[#FF4F00]"></div>
          </div>
          
          {/* Content */}
          <div className="order-1 lg:order-2">
            <span className="text-[#FF4F00] font-bold text-sm uppercase tracking-widest font-oswald">Onze Aanpak</span>
            <h2 data-testid="aanpak-title" className="font-oswald text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mt-4 mb-8">
              Eigen Volk, Vaste Waarden.
            </h2>
            <p className="text-[#E0E0E0] text-lg leading-relaxed font-manrope mb-6">
              Wij sturen geen eendagsvliegen. <strong className="text-white">Onze gasten zijn bij ons in dienst</strong> en worden geselecteerd op werklust. Het doel? Een langdurige samenwerking met Vlaamse aannemers.
            </p>
            <p className="text-[#E0E0E0] text-lg leading-relaxed font-manrope">
              Ze leren uw specifieke manier van werken kennen, of u nu een eenmanszaak bent of een grote projectontwikkelaar. Zo transformeert een tijdelijke hulp in <strong className="text-white">de vaste pijler waar u blindelings op kunt bouwen.</strong>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// SECTION 4: DIENSTEN (8 CARDS)
const DienstenSection = () => {
  const diensten = [
    {
      id: "ruwbouw",
      icon: HardHat,
      title: "De Werfassistent",
      subtitle: "Ruwbouw & Constructie",
      image: "https://images.unsplash.com/photo-1673978483810-ad14e573eece?w=800&q=80",
      tasks: [
        "Opperen van stenen",
        "Mortel en beton draaien",
        "Bekisting-support",
        "IJzervlechten assistentie",
        "Sleuven slijpen & kappen",
        "Puin ruimen"
      ]
    },
    {
      id: "wegenwerken",
      icon: Truck,
      title: "Infrastructuur Support",
      subtitle: "Wegenwerken & Grond",
      image: "https://images.unsplash.com/photo-1593436878048-92622a77d315?w=800&q=80",
      tasks: [
        "Signalisatie plaatsen",
        "Handmatig grondverzet",
        "Klinkers & stabilisé aanvoeren",
        "Tuinaanleg",
        "Omheiningen"
      ]
    },
    {
      id: "dak",
      icon: Building,
      title: "Hoogte Assistentie",
      subtitle: "Dak- & Gevelwerken",
      image: "https://images.unsplash.com/photo-1726589004565-bedfba94d3a2?w=800&q=80",
      tasks: [
        "Pannen opperen naar de stelling",
        "Afbraak oude dakbedekking",
        "Isolatie aanvoeren",
        "Dakgoten reinigen",
        "Voorbereiding gevelreiniging"
      ]
    },
    {
      id: "nieuwbouw",
      icon: Building,
      title: "De Handlanger",
      subtitle: "Nieuwbouw, Afwerking & Schilder",
      image: "https://images.unsplash.com/photo-1702392183172-17fdef8002b4?w=800&q=80",
      tasks: [
        "Gyproc platen dragen",
        "Vloerdergast",
        "Schilderhulp (schuren/afplakken)",
        "Zwaar sanitair sjouwen"
      ]
    },
    {
      id: "opkuis",
      icon: Sparkles,
      title: "De Opleveringshulp",
      subtitle: "Opkuis & Oplevering",
      image: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&q=80",
      tasks: [
        "Grote bouwopkuis",
        "Verwijderen stickers & folies",
        "Cementsluier verwijderen",
        "Stofvrij dweilen",
        "Bezemschoon opleveren"
      ]
    },
    {
      id: "verhuis",
      icon: Package,
      title: "Interne Sloop & Ontruiming",
      subtitle: "Verhuis, Leegmaking & Demontage",
      image: "https://images.unsplash.com/photo-1763951515641-12637c29d176?w=800&q=80",
      tasks: [
        "Zwaar meubilair tillen",
        "Kantoorpanden ontruimen",
        "Vloerbekleding & plafonds uitbreken",
        "Keukens demonteren",
        "Sloopafval sorteren"
      ]
    },
    {
      id: "magazijn",
      icon: Wrench,
      title: "Logistiek & Onderhoud",
      subtitle: "Koerman, Magazijn & Reiniging",
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80",
      tasks: [
        "Terreinbeheer koer",
        "Containers laden/lossen",
        "Orderpicking",
        "Industriële reiniging fabriekshallen",
        "Machines grofweg schoonmaken"
      ]
    },
    {
      id: "zonnepanelen",
      icon: Sun,
      title: "Zwaar Labeur op Maat",
      subtitle: "Zonnepanelen & Events",
      image: "https://images.unsplash.com/photo-1653315918311-5bc544495f2f?w=800&q=80",
      tasks: [
        "Zonnepanelen veilig opperen",
        "Ballast dragen plat dak",
        "Tentenbouw & podia opzetten",
        "Event logistiek & dranghekken"
      ]
    },
    {
      id: "tuinaanleg",
      icon: TreePine,
      title: "Spierkracht voor de Tuinaannemer",
      subtitle: "Tuinaanleg & Groenbeheer",
      image: "https://images.pexels.com/photos/7728706/pexels-photo-7728706.jpeg?w=800",
      tasks: [
        "Zwaar kruiwagenwerk (teelaarde, stabilisé, grind)",
        "Handmatig plantputten en sleuven graven",
        "Graszoden en bomen helpen aanplanten",
        "Snoei- en groenafval verslepen en verwerken",
        "Afsluitingen en keerwanden helpen plaatsen"
      ]
    },
    {
      id: "grondwerken",
      icon: Shovel,
      title: "Handkracht naast de Graafmachine",
      subtitle: "Grond- & Infrawerken",
      image: "https://images.unsplash.com/photo-1649829726631-fcd218631ab5?w=800&q=80",
      tasks: [
        "Sleuven trekken op moeilijk bereikbare plaatsen",
        "Kraan-assistentie (aanpikken, peilen, buizen leggen)",
        "Zware rijplaten en werfhekken plaatsen",
        "Wachtbuizen en riolering assisteren",
        "Aantrillen van de ondergrond"
      ]
    },
    {
      id: "zwembadbouw",
      icon: Waves,
      title: "Zwaar Labeur in Stadstuinen",
      subtitle: "Zwembadbouw & Wellness",
      image: "https://images.unsplash.com/photo-1766595680992-31635890cf70?w=800&q=80",
      tasks: [
        "Handmatig uitgraven van de zwembadbodem",
        "Kruiwagens beton en stabilisé naar achtertuinen rijden",
        "Zware betonblokken (stepoc) en filtersystemen sjouwen",
        "Sleuven graven voor technische leidingen"
      ]
    },
    {
      id: "hvac",
      icon: Thermometer,
      title: "Voorbereiding & Tilwerk",
      subtitle: "HVAC, Sanitair & Technieken",
      image: "https://images.unsplash.com/photo-1768321916212-17ae334a3d63?w=800&q=80",
      tasks: [
        "Warmtepompen, airco's en zware radiatoren trapopwaarts tillen",
        "Kilometers sleuven slijpen en kappen voor leidingen",
        "Assistentie bij diamantboringen (waterzuigen en afvoer)",
        "Puin ruimen voor de installateur"
      ]
    },
    {
      id: "calamiteiten",
      icon: AlertTriangle,
      title: "Brand- & Waterschade Interventie",
      subtitle: "Calamiteiten & Schade-opkuis",
      image: "https://images.unsplash.com/photo-1748816964372-8a73dbb4bb7a?w=800&q=80",
      tasks: [
        "Noodopkuis en leegpompen van kelders",
        "Zware en natte inboedel afvoeren in containers",
        "Beschadigde gyproc, vloeren en plafonds strippen",
        "Geur- en roetvoorbereiding (grof reinigen)"
      ]
    },
    {
      id: "stellingbouw",
      icon: Layers,
      title: "Pure Logistiek",
      subtitle: "Stellingbouw & Wegsignalisatie",
      image: "https://images.unsplash.com/photo-1755943807913-5f999c562f45?w=800&q=80",
      tasks: [
        "Non-stop opperen van stalen buizen en vloerdelen",
        "Assistentie bij montage en demontage stellingen",
        "Honderden verkeerskegels en tijdelijke borden uitzetten",
        "Moddervrij borstelen van het wegdek rondom werven"
      ]
    }
  ];

  return (
    <section id="diensten" data-testid="diensten-section" className="py-20 md:py-32 bg-[#1A1A1A]">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#FF4F00] font-bold text-sm uppercase tracking-widest font-oswald">Onze Diensten</span>
          <h2 data-testid="diensten-title" className="font-oswald text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mt-4">
            Multi-Inzetbare Spierkracht voor Elk Project.
          </h2>
        </div>
        
        {/* Services Grid - 14 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {diensten.map((dienst) => (
            <div 
              key={dienst.id}
              data-testid={`dienst-card-${dienst.id}`}
              className="group relative bg-[#121212] border border-zinc-800 hover:border-[#FF4F00] transition-all duration-300 overflow-hidden"
            >
              {/* Image with overlay */}
              <div className="relative h-40 overflow-hidden">
                <img 
                  src={dienst.image}
                  alt={dienst.subtitle}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/60 to-transparent"></div>
                <div className="absolute top-3 left-3">
                  <div className="bg-[#FF4F00] p-2">
                    <dienst.icon className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-5">
                <span className="text-[#FF4F00] text-xs font-bold uppercase tracking-wider">{dienst.subtitle}</span>
                <h3 className="font-oswald text-lg font-bold text-white uppercase tracking-wide mt-1 mb-3">
                  {dienst.title}
                </h3>
                <ul className="space-y-1.5">
                  {dienst.tasks.map((task, idx) => (
                    <li key={idx} className="text-[#E0E0E0] text-sm flex items-start gap-2">
                      <span className="text-[#FF4F00] mt-0.5">•</span>
                      {task}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      question: "Wat is de minimale afname?",
      answer: "Wij zijn flexibel: u kunt al vanaf 1 persoon inzetten. Voor grotere projecten schalen we mee met uw noden."
    },
    {
      question: "Wat is het tarief?",
      answer: "Een vast tarief van €40/u per persoon, inclusief alle sociale lasten en verzekeringen. U ontvangt enkel een overzichtelijke factuur."
    },
    {
      question: "Hoe snel zijn jullie ter plaatse?",
      answer: "Wij zijn actief in heel West- en Oost-Vlaanderen en kunnen doorgaans binnen 24-48 uur op locatie zijn."
    },
    {
      question: "In welke regio's werken jullie?",
      answer: "Wij bedienen Damme, Brugge, Knokke-Heist, Gent, Roeselare, Kortrijk, Oostende, Eeklo, Maldegem en heel West- en Oost-Vlaanderen."
    }
  ];

  return (
    <section id="faq" data-testid="faq-section" className="py-20 md:py-32 bg-[#121212]">
      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-[#FF4F00] font-bold text-sm uppercase tracking-widest font-oswald">FAQ</span>
          <h2 data-testid="faq-title" className="font-oswald text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mt-4">
            Veelgestelde Vragen
          </h2>
        </div>
        
        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              data-testid={`faq-item-${index}`}
              className="border border-zinc-800 bg-[#1A1A1A] px-6"
            >
              <AccordionTrigger className="font-oswald text-lg font-bold text-white uppercase tracking-wide hover:text-[#FF4F00] hover:no-underline py-5">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-[#E0E0E0] font-manrope pb-5">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    bedrijfsnaam: '',
    naam: '',
    email: '',
    telefoon: '',
    dienst: '',
    locatie: '',
    bericht: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({
          bedrijfsnaam: '',
          naam: '',
          email: '',
          telefoon: '',
          dienst: '',
          locatie: '',
          bericht: ''
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-[#1A1A1A]">
      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-[#FF4F00] font-bold text-sm uppercase tracking-widest font-oswald">Contact</span>
          <h2 data-testid="contact-title" className="font-oswald text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mt-4">
            Vraag Vrijblijvend een Offerte
          </h2>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="bg-[#121212] border border-zinc-800 p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bedrijfsnaam */}
            <div className="md:col-span-2">
              <label className="block font-oswald text-sm font-bold text-white uppercase tracking-wider mb-2">
                Bedrijfsnaam
              </label>
              <input
                type="text"
                name="bedrijfsnaam"
                value={formData.bedrijfsnaam}
                onChange={handleChange}
                data-testid="contact-company-input"
                className="w-full bg-[#1A1A1A] border border-zinc-700 focus:border-[#FF4F00] text-white px-4 py-4 outline-none transition-colors font-manrope"
                placeholder="Uw bedrijfsnaam"
              />
            </div>

            {/* Contactpersoon */}
            <div>
              <label className="block font-oswald text-sm font-bold text-white uppercase tracking-wider mb-2">
                Contactpersoon *
              </label>
              <input
                type="text"
                name="naam"
                value={formData.naam}
                onChange={handleChange}
                required
                data-testid="contact-name-input"
                className="w-full bg-[#1A1A1A] border border-zinc-700 focus:border-[#FF4F00] text-white px-4 py-4 outline-none transition-colors font-manrope"
                placeholder="Uw naam"
              />
            </div>

            {/* Telefoon */}
            <div>
              <label className="block font-oswald text-sm font-bold text-white uppercase tracking-wider mb-2">
                Telefoonnummer *
              </label>
              <input
                type="tel"
                name="telefoon"
                value={formData.telefoon}
                onChange={handleChange}
                required
                data-testid="contact-phone-input"
                className="w-full bg-[#1A1A1A] border border-zinc-700 focus:border-[#FF4F00] text-white px-4 py-4 outline-none transition-colors font-manrope"
                placeholder="0470 00 00 00"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block font-oswald text-sm font-bold text-white uppercase tracking-wider mb-2">
                E-mailadres
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                data-testid="contact-email-input"
                className="w-full bg-[#1A1A1A] border border-zinc-700 focus:border-[#FF4F00] text-white px-4 py-4 outline-none transition-colors font-manrope"
                placeholder="uw@email.be"
              />
            </div>

            {/* Locatie */}
            <div>
              <label className="block font-oswald text-sm font-bold text-white uppercase tracking-wider mb-2">
                Locatie Werf
              </label>
              <input
                type="text"
                name="locatie"
                value={formData.locatie}
                onChange={handleChange}
                data-testid="contact-location-input"
                className="w-full bg-[#1A1A1A] border border-zinc-700 focus:border-[#FF4F00] text-white px-4 py-4 outline-none transition-colors font-manrope"
                placeholder="Stad of gemeente"
              />
            </div>

            {/* Dienst */}
            <div className="md:col-span-2">
              <label className="block font-oswald text-sm font-bold text-white uppercase tracking-wider mb-2">
                Gewenste Dienst
              </label>
              <select
                name="dienst"
                value={formData.dienst}
                onChange={handleChange}
                data-testid="contact-service-select"
                className="w-full bg-[#1A1A1A] border border-zinc-700 focus:border-[#FF4F00] text-white px-4 py-4 outline-none transition-colors font-manrope"
              >
                <option value="">Selecteer een dienst</option>
                <option value="ruwbouw">Ruwbouw & Constructie</option>
                <option value="wegenwerken">Wegenwerken & Grond</option>
                <option value="dak">Dak- & Gevelwerken</option>
                <option value="nieuwbouw">Nieuwbouw, Afwerking & Schilder</option>
                <option value="opkuis">Opkuis & Oplevering</option>
                <option value="verhuis">Verhuis, Leegmaking & Demontage</option>
                <option value="magazijn">Koerman, Magazijn & Reiniging</option>
                <option value="zonnepanelen">Zonnepanelen & Events</option>
                <option value="tuinaanleg">Tuinaanleg & Groenbeheer</option>
                <option value="grondwerken">Grond- & Infrawerken</option>
                <option value="zwembadbouw">Zwembadbouw & Wellness</option>
                <option value="hvac">HVAC, Sanitair & Technieken</option>
                <option value="calamiteiten">Calamiteiten & Schade-opkuis</option>
                <option value="stellingbouw">Stellingbouw & Wegsignalisatie</option>
              </select>
            </div>

            {/* Bericht */}
            <div className="md:col-span-2">
              <label className="block font-oswald text-sm font-bold text-white uppercase tracking-wider mb-2">
                Uw Bericht
              </label>
              <textarea
                name="bericht"
                value={formData.bericht}
                onChange={handleChange}
                rows={4}
                data-testid="contact-message-input"
                className="w-full bg-[#1A1A1A] border border-zinc-700 focus:border-[#FF4F00] text-white px-4 py-4 outline-none transition-colors font-manrope resize-none"
                placeholder="Beschrijf kort uw project en hoeveel personen u nodig heeft..."
              />
            </div>
          </div>

          {/* Submit Status Messages */}
          {submitStatus === 'success' && (
            <div data-testid="contact-success-message" className="mt-6 p-4 bg-green-900/50 border border-green-500 text-green-400 font-manrope">
              <CheckCircle className="inline-block w-5 h-5 mr-2" />
              Bedankt voor uw aanvraag! Wij nemen zo snel mogelijk contact met u op.
            </div>
          )}
          {submitStatus === 'error' && (
            <div data-testid="contact-error-message" className="mt-6 p-4 bg-red-900/50 border border-red-500 text-red-400 font-manrope">
              <AlertTriangle className="inline-block w-5 h-5 mr-2" />
              Er is iets misgegaan. Bel ons gerust direct op {PHONE_DISPLAY}.
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-8">
            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="contact-submit-btn"
              className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#FF4F00] text-white font-bold text-lg uppercase tracking-wider px-10 py-5 industrial-btn hover:bg-[#E64500] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  Verzenden...
                </>
              ) : (
                <>
                  <Send className="w-6 h-6" />
                  Aanvraag Versturen
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

// SECTION 5: FINAL CTA with Local SEO
const FinalCTASection = () => {
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=Hallo%2C%20ik%20zoek%20werfassistenten.`;
  
  return (
    <section data-testid="final-cta-section" className="py-20 md:py-32 bg-[#FF4F00] relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(45deg, #000 0, #000 10px, transparent 10px, transparent 20px)`
        }}></div>
      </div>
      
      <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center relative z-10">
        <h2 data-testid="final-cta-title" className="font-oswald text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-6">
          Klaar om Uw Vakmensen te Ontlasten?
        </h2>
        <p className="text-white/90 text-lg md:text-xl mb-10 max-w-2xl mx-auto font-manrope">
          Boek vandaag nog uw vaste rechterhand. Wij zijn direct inzetbaar. Bel ons of stuur een bericht, en wij regelen de rest.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a 
            href="tel:+32470987076"
            data-testid="final-cta-call-btn"
            className="inline-flex items-center justify-center gap-3 bg-[#121212] text-white font-bold text-lg uppercase tracking-wider px-10 py-5 industrial-btn hover:bg-black transition-colors"
          >
            <Phone className="w-6 h-6" />
            Bel Nu: {PHONE_DISPLAY}
          </a>
          <a 
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-whatsapp-btn"
            className="inline-flex items-center justify-center gap-3 border-2 border-white text-white font-bold text-lg uppercase tracking-wider px-10 py-5 hover:bg-white hover:text-[#FF4F00] transition-colors"
          >
            <MessageCircle className="w-6 h-6" />
            WhatsApp Ons
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer with Local SEO Block
const Footer = () => {
  const currentYear = 2026;
  
  return (
    <footer data-testid="footer" className="bg-[#0A0A0A]">
      {/* Local SEO Region Block */}
      <div className="bg-[#121212] py-8 border-b border-zinc-900">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <p className="text-zinc-400 text-sm md:text-base text-center font-manrope leading-relaxed">
            <strong className="text-white">Werkvloot levert mankracht en werfassistenten in de ruime regio's:</strong>{" "}
            Damme, Brugge, Knokke-Heist, Zeebrugge, Blankenberge, Oostende, Roeselare, Kortrijk, Torhout, Tielt, Eeklo, Maldegem, Aalter, Deinze, Gent en ver daarbuiten in heel <strong className="text-[#FF4F00]">West-Vlaanderen</strong> en <strong className="text-[#FF4F00]">Oost-Vlaanderen</strong>.
          </p>
        </div>
      </div>
      
      {/* Main Footer */}
      <div className="py-12">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Logo & Slogan */}
            <div>
              <div className="font-oswald text-3xl font-bold text-white uppercase tracking-tight mb-2">
                WERKVLOOT
              </div>
              <p className="text-zinc-500 font-manrope">De Vaste Rechterhand op de Werf</p>
            </div>
            
            {/* Contact Info */}
            <div>
              <h4 className="font-oswald text-sm font-bold text-white uppercase tracking-widest mb-4">Contact</h4>
              <div className="space-y-3 text-zinc-400">
                <a href="tel:+32470987076" className="flex items-center gap-2 hover:text-[#FF4F00] transition-colors font-bold text-[#FF4F00]">
                  <Phone className="w-4 h-4" />
                  {PHONE_DISPLAY}
                </a>
                <a href="mailto:info@werkvloot.be" className="flex items-center gap-2 hover:text-[#FF4F00] transition-colors">
                  <Mail className="w-4 h-4" />
                  {EMAIL}
                </a>
              </div>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="font-oswald text-sm font-bold text-white uppercase tracking-widest mb-4">Bedrijfsinfo</h4>
              <div className="space-y-2 text-zinc-400 text-sm">
                <p>BTW: BE0799.832.009</p>
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Damme, West-Vlaanderen
                </p>
              </div>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="border-t border-zinc-900 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-zinc-600 text-sm">
                © {currentYear} WERKVLOOT - Alle rechten voorbehouden.
              </p>
              <div className="flex gap-6">
                <a href="#diensten" className="text-zinc-500 text-sm hover:text-[#FF4F00] transition-colors">Diensten</a>
                <a href="#aanpak" className="text-zinc-500 text-sm hover:text-[#FF4F00] transition-colors">Aanpak</a>
                <a href="#faq" className="text-zinc-500 text-sm hover:text-[#FF4F00] transition-colors">FAQ</a>
                <a href="#contact" className="text-zinc-500 text-sm hover:text-[#FF4F00] transition-colors">Contact</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Navigation with Phone & Email in Header
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <nav data-testid="navigation" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#121212]/98 backdrop-blur-sm border-b border-zinc-800' : 'bg-transparent'}`}>
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#" className="font-oswald text-2xl font-bold text-white uppercase tracking-tight">
            WERKVLOOT
          </a>
          
          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6">
            <a href="#diensten" className="text-zinc-400 hover:text-white font-medium transition-colors">Diensten</a>
            <a href="#aanpak" className="text-zinc-400 hover:text-white font-medium transition-colors">Aanpak</a>
            <a href="#faq" className="text-zinc-400 hover:text-white font-medium transition-colors">FAQ</a>
            <a href="#contact" className="text-zinc-400 hover:text-white font-medium transition-colors">Contact</a>
            
            {/* Divider */}
            <div className="w-px h-6 bg-zinc-700"></div>
            
            {/* Phone - Prominent */}
            <a 
              href="tel:+32470987076"
              data-testid="nav-phone"
              className="flex items-center gap-2 text-[#FF4F00] font-bold hover:text-white transition-colors"
            >
              <Phone className="w-4 h-4" />
              {PHONE_DISPLAY}
            </a>
            
            {/* Email */}
            <a 
              href="mailto:info@werkvloot.be"
              data-testid="nav-email"
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <Mail className="w-4 h-4" />
              {EMAIL}
            </a>
          </div>
          
          {/* Mobile: Phone + Menu */}
          <div className="flex lg:hidden items-center gap-4">
            <a 
              href="tel:+32470987076"
              className="flex items-center gap-2 text-[#FF4F00] font-bold text-sm"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
            </a>
            
            {/* Mobile Menu Button */}
            <button 
              data-testid="mobile-menu-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-[#121212] border-t border-zinc-800 py-4">
            <div className="flex flex-col gap-4">
              <a href="#diensten" onClick={() => setIsMenuOpen(false)} className="text-zinc-400 hover:text-white font-medium transition-colors py-2">Diensten</a>
              <a href="#aanpak" onClick={() => setIsMenuOpen(false)} className="text-zinc-400 hover:text-white font-medium transition-colors py-2">Aanpak</a>
              <a href="#faq" onClick={() => setIsMenuOpen(false)} className="text-zinc-400 hover:text-white font-medium transition-colors py-2">FAQ</a>
              <a href="#contact" onClick={() => setIsMenuOpen(false)} className="text-zinc-400 hover:text-white font-medium transition-colors py-2">Contact</a>
              
              <div className="border-t border-zinc-800 pt-4 mt-2 space-y-3">
                <a 
                  href="tel:+32470987076"
                  className="flex items-center gap-2 text-[#FF4F00] font-bold"
                >
                  <Phone className="w-5 h-5" />
                  {PHONE_DISPLAY}
                </a>
                <a 
                  href="mailto:info@werkvloot.be"
                  className="flex items-center gap-2 text-zinc-400"
                >
                  <Mail className="w-5 h-5" />
                  {EMAIL}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Home Page
const Home = () => {
  useEffect(() => {
    initGoogleAnalytics();
  }, []);

  return (
    <div className="min-h-screen bg-[#121212]">
      <Navigation />
      <main>
        <HeroSection />
        <TrustBar />
        <AanpakSection />
        <DienstenSection />
        <FAQSection />
        <ContactFormSection />
        <FinalCTASection />
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
